/**
 * Encounters runtime (M2-15; plan §2.3, §2.9 `encounters.json`; spec §9.2-§9.3): turns the pure encounter director
 * (M2-13, `src/encounters/*`) into ships and tracks every group until it is gone.
 *
 * - **end stage** (first in `end`): steps the director with inputs from the mirror, the docking phase and the
 *   stations; a raider or repo request becomes ships through `composition` and `spawnPositions` (2-2.6 km from the
 *   player, outside the field, ≥ `stationExclusion` off every station's hull, SC-5e1) spawned with
 *   `spawnShip(..., { team: RAIDER_TEAM, profile, rotation })`, each tagged with its group (`EncounterTag.group`);
 *   pending clear-raiders contract groups spawn around their marker when the player comes within
 *   `contracts.json clearRaiders.spawnWithin`; a live group whose every ship is farther than `despawnDistance` for
 *   `ENCOUNTER_TUNING.farDespawnSeconds` despawns quietly (a contract group goes back to pending).
 * - **deaths stage**: this tick's M1 `kill` events count group losses; the last loss emits `groupDefeated`
 *   (a = group id, b = `ENCOUNTER_KIND`, c = ships; a contract group's contract id through `contractOf`). Any
 *   other M1 `spawn` clears the tag of the (possibly recycled) entity.
 * - **Deltas:** the director's timers and every clear-raiders group not yet defeated (live ships are never saved:
 *   a load resumes docked and the group waits at its marker again).
 * - **The career in the Reach** (additive, M3 plan R22h): `bindEncounterSpawnHook` hands the director's groups (raiders
 *   and the repo crew) to the M2 bridge, which spawns them through the Reach session in whatever system the player is
 *   in. While the hook is closed (fast travel, a system switch) the player is not "in space" for the director, so a due
 *   request waits; a hooked runtime releases a group whose ships all vanished without a kill (the Reach despawned them)
 *   at once. Unbound, nothing changes.
 *
 * Randomness comes from the space RNG (career seed) only, never `sim.rng` (plan §2.11). Allocation-free per tick
 * (D-014); spawning and despawning ships allocate inside Rapier's binding and are rare events (D-024).
 */
import { commitRemovals } from 'bitecs'
import { createSpawnRequests, type SpawnRequests } from '../ai/waves'
import { lookRotation } from '../ai/steering'
import { EV, eventSlot, firstReadable } from '../combat/events'
import { logger } from '../core/logger'
import type { Quat, Vec3 } from '../core/math'
import { nextFloat, range } from '../core/rng'
import {
  REQUEST_NONE,
  composition,
  contractRequest,
  createDirectorInput,
  createDirectorState,
  createEncounterRequest,
  exportDirectorState,
  importDirectorState,
  stepDirector,
  type DirectorInput,
  type EncounterDirectorState,
  type EncounterRequest,
  type RaidTarget,
} from '../encounters/director'
import { spawnPositions } from '../encounters/placement'
import { despawnShip, RAIDER_TEAM, spawnShip, type Sim, type SpawnOptions } from '../sim'
import { Life, MAX_ENTITIES, Pos64, Rot } from '../world/components'
import { EncounterTag } from './components'
import {
  createEncounterGroups,
  emptyEncounterDeltas,
  type DockingRuntime,
  type EncounterDeltas,
  type EncounterGroups,
  type EncountersRuntime,
  type SpaceContext,
  type StationsRuntime,
  type Vec3Tuple,
} from './contracts'
import { ENCOUNTER_KIND, SEV, emitSpaceEvent } from './events'

/**
 * Encounter tuning without a data field yet. Mutable so the dev panel can bind it.
 * D-TBD: move into encounters.json (`farDespawnSeconds`, `contractScatter`) when that schema is next owned.
 */
export const ENCOUNTER_TUNING = {
  /** A live group with every ship beyond `despawnDistance` for this long (game seconds) despawns quietly: 10 min. */
  farDespawnSeconds: 600,
  /** Contract-group ships appear within this distance of their marker (m). */
  contractScatter: 300,
}

/**
 * Additive (M3 R22h): where the director's groups spawn when the career runs in the Reach. The M2 bridge
 * (`src/bridge/m2/raiders.ts`) implements it over `session.spawnGroup`.
 */
export interface EncounterSpawnHook {
  /** False while the player may not meet a directed group (fast travel, a system switch). Allocation-free. */
  open(): boolean
  /**
   * Spawns one director group. `req` holds its ships (`defIndex`, `profile`) and M2's placement (positions 2-2.6 km
   * from the player, clear of the field and the stations, facing the player); `kind` is its `ENCOUNTER_KIND`. Writes
   * the spawned entities into `out` and returns how many. A rare event: may allocate.
   */
  spawn(req: SpawnRequests, kind: number, out: Int32Array): number
  /** M10 P4b6a: the raid's target (the flown hull's band, kit slots), or null; raids scale to it. Rare (a spawn). */
  target?(): RaidTarget | null
}

/** Finished groups remembered for `contractOf` (a `groupDefeated` may be read a few ticks after the slot is reused). */
const HISTORY = 32
/** Candidate positions per contract ship before the last one is used anyway. */
const SCATTER_ATTEMPTS = 16

interface EncountersState {
  readonly ctx: SpaceContext
  readonly stations: StationsRuntime
  readonly docking: DockingRuntime
  readonly groups: EncounterGroups
  /** Contract tier per slot (index into `encounters.json contractTiers`). */
  readonly tier: Int8Array
  /** Game time a live group went out of range, or -1. */
  readonly farSince: Float64Array
  /** Scratch: nearest squared distance of each slot's ships to the player this tick. */
  readonly nearest2: Float64Array
  readonly director: EncounterDirectorState
  readonly input: DirectorInput
  readonly request: EncounterRequest
  readonly spawn: SpawnRequests
  /** Game time of the last undock (`notifyUndock`), or -1. */
  undockedAt: number
  /** Combat ring cursor: the first event not yet read for kills and foreign spawns. */
  cursor: number
  readonly historyId: Int32Array
  readonly historyContract: (string | null)[]
  historyHead: number
  /** The Reach spawn hook (M3 R22h), or null: M2 spawns its own ships. */
  hook: EncounterSpawnHook | null
  /** Scratch: the entities the hook spawned. */
  readonly hookEids: Int32Array
}

/** Each runtime's state, for `bindEncounterSpawnHook` (the runtime interface stays unchanged). */
const states = new WeakMap<EncountersRuntime, EncountersState>()

/**
 * Additive (M3 R22h): routes `runtime`'s director groups through `hook` (null restores M2's own spawns). Returns
 * false when `runtime` was not made by `createEncountersRuntime`.
 */
export function bindEncounterSpawnHook(runtime: EncountersRuntime, hook: EncounterSpawnHook | null): boolean {
  const st = states.get(runtime)
  if (st === undefined) return false
  st.hook = hook
  return true
}

// Module scratch (D-014).
const playerPos: Vec3 = { x: 0, y: 0, z: 0 }
const playerFwd: Vec3 = { x: 0, y: 0, z: -1 }
const spawnRot: Quat = { x: 0, y: 0, z: 0, w: 1 }
/** `team` is set at spawn time (reading sim exports at module load breaks tests that mock the sim module). */
const spawnOpts: SpawnOptions = { profile: 0, rotation: spawnRot }
const doomed = new Int32Array(MAX_ENTITIES)

/** The player's position and nose (world unit vector) into the module scratch. */
function loadPlayer(eid: number): void {
  playerPos.x = Pos64.x[eid]
  playerPos.y = Pos64.y[eid]
  playerPos.z = Pos64.z[eid]
  const qx = Rot.x[eid]
  const qy = Rot.y[eid]
  const qz = Rot.z[eid]
  const qw = Rot.w[eid]
  playerFwd.x = -2 * (qx * qz + qw * qy)
  playerFwd.y = -2 * (qy * qz - qw * qx)
  playerFwd.z = -(1 - 2 * (qx * qx + qy * qy))
}

/**
 * Distance from the player (loaded) to the nearest station's hull, `|p − centre| − R` (scale SC-5e1: the bounding
 * sphere, `StationRuntime.radius`; negative inside it); +Infinity without stations. Per tick: Math.sqrt, not
 * Math.hypot (which allocates per call in V8, D-920).
 */
function nearestStationDistance(st: EncountersState): number {
  const list = st.stations.list
  let best = Number.POSITIVE_INFINITY
  for (let i = 0; i < list.length; i++) {
    const s = list[i]
    const p = s.pos
    const dx = p.x - playerPos.x
    const dy = p.y - playerPos.y
    const dz = p.z - playerPos.z
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz) - s.radius
    if (d < best) best = d
  }
  return best
}

/** Slot of live group `id`, or -1. */
function slotOf(st: EncountersState, id: number): number {
  if (id <= 0) return -1
  const g = st.groups
  for (let i = 0; i < g.capacity; i++) if (g.active[i] === 1 && g.id[i] === id) return i
  return -1
}

/** Slot of the contract group of `contractId`, or -1. */
function slotOfContract(st: EncountersState, contractId: string): number {
  const g = st.groups
  for (let i = 0; i < g.capacity; i++) if (g.active[i] === 1 && g.contractId[i] === contractId) return i
  return -1
}

function freeSlotIndex(st: EncountersState): number {
  const g = st.groups
  for (let i = 0; i < g.capacity; i++) if (g.active[i] === 0) return i
  return -1
}

/** Remembers a finished group's contract (`contractOf` keeps answering for ids still in the event ring). */
function remember(st: EncountersState, id: number, contractId: string | null): void {
  if (id <= 0) return
  const h = st.historyHead % HISTORY
  st.historyId[h] = id
  st.historyContract[h] = contractId
  st.historyHead++
}

/** Clears slot `i` (the group is gone). */
function releaseSlot(st: EncountersState, i: number): void {
  const g = st.groups
  remember(st, g.id[i], g.contractId[i])
  g.active[i] = 0
  g.id[i] = 0
  g.kind[i] = 0
  g.total[i] = 0
  g.alive[i] = 0
  g.spawned[i] = 0
  g.contractId[i] = null
  st.tier[i] = 0
  st.farSince[i] = -1
}

/** A contract group back to waiting at its marker (its ships are gone without a defeat). */
function toPending(st: EncountersState, i: number): void {
  const g = st.groups
  remember(st, g.id[i], g.contractId[i])
  g.id[i] = 0
  g.total[i] = 0
  g.alive[i] = 0
  g.spawned[i] = 0
  st.farSince[i] = -1
}

/** Quietly removes every ship of group `id` (no `kill`, no wreck, no `groupDefeated`). */
function despawnGroupShips(sim: Sim, id: number): void {
  const ships = sim.ships.dense
  let n = 0
  for (let i = 0; i < ships.length; i++) {
    const eid = ships[i]
    if (EncounterTag.group[eid] === id) doomed[n++] = eid
  }
  for (let k = 0; k < n; k++) {
    const eid = doomed[k]
    EncounterTag.group[eid] = 0
    despawnShip(sim, eid)
  }
  if (n > 0) commitRemovals(sim.world)
}

/**
 * Spawns the `st.spawn` ships (positions and profiles already written) as group `id` of `kind` into slot `slot`,
 * tags them, fills the slot and emits `encounterSpawned` at the group centre (`cx`, `cy`, `cz`).
 */
function spawnGroup(st: EncountersState, sim: Sim, slot: number, id: number, kind: number, cx: number, cy: number, cz: number): void {
  const req = st.spawn
  const g = st.groups
  spawnOpts.team = RAIDER_TEAM
  let n = 0
  for (let k = 0; k < req.count; k++) {
    spawnRot.x = req.qx[k]
    spawnRot.y = req.qy[k]
    spawnRot.z = req.qz[k]
    spawnRot.w = req.qw[k]
    spawnOpts.profile = req.profile[k]
    const eid = spawnShip(sim, req.defIndex[k], req.x[k], req.y[k], req.z[k], false, spawnOpts)
    EncounterTag.group[eid] = id
    n++
  }
  g.active[slot] = 1
  g.id[slot] = id
  g.kind[slot] = kind
  g.total[slot] = n
  g.alive[slot] = n
  g.spawned[slot] = 1
  g.x[slot] = cx
  g.y[slot] = cy
  g.z[slot] = cz
  st.farSince[slot] = -1
  emitSpaceEvent(st.ctx.events, SEV.encounterSpawned, -1, id, kind, n, cx, cy, cz, 0, 0)
}

/** A director request (raiders or repo crew) → ships 2-2.6 km from the player (loaded), clear of the field and stations. */
function spawnDirected(st: EncountersState, sim: Sim): void {
  const ctx = st.ctx
  const req = st.spawn
  const n = composition(st.request, ctx.data.encounters, ctx.game, req, st.hook?.target?.() ?? null)
  if (n === 0) return
  const slot = freeSlotIndex(st)
  if (slot < 0) {
    logger.warn('Encounters: every group slot is in use; the request is dropped')
    return
  }
  spawnPositions(playerPos, playerFwd, n, ctx.data.encounters, st.stations.list, sim.insideField, ctx.rng, req)
  if (st.hook !== null) {
    spawnHooked(st, st.hook, slot, st.request.group, st.request.kind)
    return
  }
  let cx = 0
  let cy = 0
  let cz = 0
  for (let k = 0; k < req.count; k++) {
    cx += req.x[k]
    cy += req.y[k]
    cz += req.z[k]
  }
  spawnGroup(st, sim, slot, st.request.group, st.request.kind, cx / req.count, cy / req.count, cz / req.count)
}

/**
 * The hook spawns the `st.spawn` group (M3 R22h); its ships are tagged as group `id` of `kind` in slot `slot`, and
 * `encounterSpawned` names their centre. Nothing is recorded when the hook spawned no ship.
 */
function spawnHooked(st: EncountersState, hook: EncounterSpawnHook, slot: number, id: number, kind: number): void {
  const eids = st.hookEids
  const n = Math.min(hook.spawn(st.spawn, kind, eids), eids.length)
  if (n <= 0) return
  let cx = 0
  let cy = 0
  let cz = 0
  for (let k = 0; k < n; k++) {
    const eid = eids[k]
    EncounterTag.group[eid] = id
    cx += Pos64.x[eid]
    cy += Pos64.y[eid]
    cz += Pos64.z[eid]
  }
  cx /= n
  cy /= n
  cz /= n
  const g = st.groups
  g.active[slot] = 1
  g.id[slot] = id
  g.kind[slot] = kind
  g.total[slot] = n
  g.alive[slot] = n
  g.spawned[slot] = 1
  g.x[slot] = cx
  g.y[slot] = cy
  g.z[slot] = cz
  st.farSince[slot] = -1
  emitSpaceEvent(st.ctx.events, SEV.encounterSpawned, -1, id, kind, n, cx, cy, cz, 0, 0)
}

/** Writes contract-group positions for `st.spawn.count` ships within `contractScatter` of (mx, my, mz), outside the field, facing the player. */
function scatterAroundMarker(st: EncountersState, sim: Sim, mx: number, my: number, mz: number): void {
  const req = st.spawn
  const rng = st.ctx.rng
  const scatter = ENCOUNTER_TUNING.contractScatter
  for (let k = 0; k < req.count; k++) {
    let x = mx
    let y = my
    let z = mz
    let placed = false
    for (let attempt = 0; attempt < SCATTER_ATTEMPTS && !placed; attempt++) {
      const cz = nextFloat(rng) * 2 - 1
      const s = Math.sqrt(Math.max(0, 1 - cz * cz))
      const phi = range(rng, 0, Math.PI * 2)
      const d = scatter * Math.cbrt(nextFloat(rng))
      x = mx + Math.cos(phi) * s * d
      y = my + Math.sin(phi) * s * d
      z = mz + cz * d
      placed = !sim.insideField(x, y, z)
    }
    req.x[k] = x
    req.y[k] = y
    req.z[k] = z
    lookRotation(playerPos.x - x, playerPos.y - y, playerPos.z - z, spawnRot)
    req.qx[k] = spawnRot.x
    req.qy[k] = spawnRot.y
    req.qz[k] = spawnRot.z
    req.qw[k] = spawnRot.w
  }
}

/** Pending clear-raiders groups spawn at their marker once the player (loaded) is within `spawnWithin`. */
function stepContractGroups(st: EncountersState, sim: Sim): void {
  const ctx = st.ctx
  const g = st.groups
  const within = ctx.data.contracts.types.clearRaiders.spawnWithin
  const within2 = within * within
  for (let i = 0; i < g.capacity; i++) {
    if (g.active[i] !== 1 || g.spawned[i] === 1 || g.kind[i] !== ENCOUNTER_KIND.contract) continue
    const dx = g.x[i] - playerPos.x
    const dy = g.y[i] - playerPos.y
    const dz = g.z[i] - playerPos.z
    if (dx * dx + dy * dy + dz * dz > within2) continue
    contractRequest(st.director, ctx.data.encounters, st.tier[i], st.request)
    const n = composition(st.request, ctx.data.encounters, ctx.game, st.spawn)
    if (n === 0) continue
    scatterAroundMarker(st, sim, g.x[i], g.y[i], g.z[i])
    spawnGroup(st, sim, i, st.request.group, ENCOUNTER_KIND.contract, g.x[i], g.y[i], g.z[i])
  }
}

/** Live groups with every ship beyond `despawnDistance` for `farDespawnSeconds` despawn quietly. */
function stepFarDespawn(st: EncountersState, sim: Sim, now: number): void {
  const g = st.groups
  const near2 = st.nearest2
  for (let i = 0; i < g.capacity; i++) near2[i] = Number.POSITIVE_INFINITY
  const ships = sim.ships.dense
  for (let k = 0; k < ships.length; k++) {
    const eid = ships[k]
    const id = EncounterTag.group[eid]
    if (id <= 0 || Life.alive[eid] !== 1) continue
    const slot = slotOf(st, id)
    if (slot < 0) continue
    const dx = Pos64.x[eid] - playerPos.x
    const dy = Pos64.y[eid] - playerPos.y
    const dz = Pos64.z[eid] - playerPos.z
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 < near2[slot]) near2[slot] = d2
  }
  const far = st.ctx.data.encounters.despawnDistance
  const far2 = far * far
  for (let i = 0; i < g.capacity; i++) {
    if (g.active[i] !== 1 || g.spawned[i] !== 1) continue
    if (st.hook !== null && near2[i] === Number.POSITIVE_INFINITY) {
      // In the Reach (R22h) a group whose ships all vanished without a kill was despawned there (a system switch, the
      // transient sweep): it is gone now, not after the far timer.
      if (g.kind[i] === ENCOUNTER_KIND.contract) toPending(st, i)
      else releaseSlot(st, i)
      continue
    }
    if (near2[i] <= far2) {
      st.farSince[i] = -1
      continue
    }
    if (st.farSince[i] < 0) {
      st.farSince[i] = now
      continue
    }
    if (now - st.farSince[i] < ENCOUNTER_TUNING.farDespawnSeconds) continue
    despawnGroupShips(sim, g.id[i])
    if (g.kind[i] === ENCOUNTER_KIND.contract) toPending(st, i)
    else releaseSlot(st, i)
  }
}

/** One lost ship of group `id`; the last one emits `groupDefeated` and frees the slot. */
function loseShip(st: EncountersState, id: number): void {
  const slot = slotOf(st, id)
  if (slot < 0) return
  const g = st.groups
  if (g.alive[slot] > 0) g.alive[slot]--
  if (g.alive[slot] > 0) return
  emitSpaceEvent(st.ctx.events, SEV.groupDefeated, -1, id, g.kind[slot], g.total[slot], g.x[slot], g.y[slot], g.z[slot], 0, 0)
  releaseSlot(st, slot)
}

/**
 * Reads the combat ring since the cursor: a `kill` of a tagged ship is a group loss (the tag is cleared, the entity
 * may be recycled); any other `spawn` clears the tag of its entity. Advances the cursor to the head.
 */
function scanCombat(st: EncountersState, sim: Sim): void {
  const ring = sim.events
  for (let i = firstReadable(ring, st.cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const type = ring.type[s]
    if (type === EV.kill) {
      const eid = ring.eid[s]
      if (eid < 0) continue
      const id = EncounterTag.group[eid]
      if (id <= 0) continue
      EncounterTag.group[eid] = 0
      loseShip(st, id)
    } else if (type === EV.spawn) {
      const eid = ring.eid[s]
      if (eid >= 0) EncounterTag.group[eid] = 0
    }
  }
  st.cursor = ring.head
}

/** The deaths stage (plan §2.3 row 6, after `salvage.deaths`). */
function encountersDeaths(st: EncountersState, sim: Sim): void {
  scanCombat(st, sim)
}

/** The end stage (plan §2.3 row 7-8, first in `end`). */
function encountersEnd(st: EncountersState, sim: Sim): void {
  // Foreign spawns since the deaths stage (e.g. a sandbox wave) lose any stale tag before ours are written.
  scanCombat(st, sim)
  const ctx = st.ctx
  const mirror = ctx.mirror
  const eid = sim.playerEid
  const alive = eid >= 0 && Life.alive[eid] === 1
  const phase = st.docking.readout.phase
  const input = st.input
  input.now = mirror.gameTime
  // A closed Reach hook (fast travel, a system switch) holds everything that needs the player in normal flight.
  input.inSpace = alive && phase !== 'docked' && (st.hook === null || st.hook.open())
  input.autodocking = phase === 'autodock'
  input.cargoValue = mirror.cargoValue
  input.collections = mirror.collections
  input.repoPaused = mirror.repoPaused
  input.undockedAt = st.undockedAt
  input.nearStation = Number.POSITIVE_INFINITY
  if (alive) {
    // The player never belongs to a group (a recycled entity id may carry a stale tag).
    EncounterTag.group[eid] = 0
    loadPlayer(eid)
    input.nearStation = nearestStationDistance(st)
  }
  const kind = stepDirector(st.director, input, ctx.data.encounters, ctx.rng, st.request)
  if (input.inSpace) {
    if (kind !== REQUEST_NONE) spawnDirected(st, sim)
    stepContractGroups(st, sim)
    stepFarDespawn(st, sim, mirror.gameTime)
  }
  // Our own spawn events are not foreign spawns.
  st.cursor = sim.events.head
}

/** Registers a pending clear-raiders group at `marker` (no-op when the contract already has one). */
function addContractGroup(st: EncountersState, contractId: string, mx: number, my: number, mz: number, tier: number): void {
  if (slotOfContract(st, contractId) >= 0) return
  const slot = freeSlotIndex(st)
  if (slot < 0) {
    logger.warn(`Encounters: no group slot for contract ${contractId}`)
    return
  }
  const g = st.groups
  g.active[slot] = 1
  g.id[slot] = 0
  g.kind[slot] = ENCOUNTER_KIND.contract
  g.total[slot] = 0
  g.alive[slot] = 0
  g.spawned[slot] = 0
  g.x[slot] = mx
  g.y[slot] = my
  g.z[slot] = mz
  g.contractId[slot] = contractId
  st.tier[slot] = tier
  st.farSince[slot] = -1
}

/** Removes every group (live ships despawn quietly) and forgets the history: a load or a new career. */
function clearGroups(st: EncountersState): void {
  const g = st.groups
  const sim = st.ctx.sim
  for (let i = 0; i < g.capacity; i++) {
    if (g.active[i] !== 1) continue
    if (g.spawned[i] === 1) despawnGroupShips(sim, g.id[i])
    releaseSlot(st, i)
  }
  st.historyId.fill(0)
  st.historyContract.fill(null)
  st.historyHead = 0
}

export function createEncountersRuntime(ctx: SpaceContext, stations: StationsRuntime, docking: DockingRuntime): EncountersRuntime {
  const cfg = ctx.data.encounters
  const capacity = cfg.maxGroups
  let maxShips = cfg.repo.ships.length
  for (const wave of ctx.game.waves.waves) maxShips = Math.max(maxShips, wave.ships.length)
  const st: EncountersState = {
    ctx,
    stations,
    docking,
    groups: createEncounterGroups(capacity),
    tier: new Int8Array(capacity),
    farSince: new Float64Array(capacity).fill(-1),
    nearest2: new Float64Array(capacity),
    director: createDirectorState(),
    input: createDirectorInput(),
    request: createEncounterRequest(),
    spawn: createSpawnRequests(Math.max(1, maxShips)),
    undockedAt: -1,
    cursor: ctx.sim.events.head,
    historyId: new Int32Array(HISTORY),
    historyContract: new Array<string | null>(HISTORY).fill(null),
    historyHead: 0,
    hook: null,
    hookEids: new Int32Array(Math.max(1, maxShips)),
  }
  const runtime: EncountersRuntime = {
    groups: st.groups,
    spawnContractGroup(contractId: string, marker: Readonly<Vec3Tuple>, tier: number): void {
      addContractGroup(st, contractId, marker[0], marker[1], marker[2], tier)
    },
    cancelContractGroup(contractId: string): void {
      const slot = slotOfContract(st, contractId)
      if (slot < 0) return
      if (st.groups.spawned[slot] === 1) despawnGroupShips(ctx.sim, st.groups.id[slot])
      releaseSlot(st, slot)
    },
    contractOf(group: number): string | null {
      const slot = slotOf(st, group)
      if (slot >= 0) return st.groups.contractId[slot]
      if (group <= 0) return null
      for (let i = 0; i < HISTORY; i++) if (st.historyId[i] === group) return st.historyContract[i]
      return null
    },
    notifyUndock(): void {
      st.undockedAt = ctx.mirror.gameTime
    },
    exportDeltas(): EncounterDeltas {
      const deltas = emptyEncounterDeltas()
      exportDirectorState(st.director, deltas)
      const g = st.groups
      for (let i = 0; i < g.capacity; i++) {
        const contractId = g.contractId[i]
        if (g.active[i] !== 1 || g.kind[i] !== ENCOUNTER_KIND.contract || contractId === null) continue
        deltas.groups.push({ contractId, marker: [g.x[i], g.y[i], g.z[i]], tier: st.tier[i] })
      }
      return deltas
    },
    importDeltas(deltas: EncounterDeltas): void {
      clearGroups(st)
      importDirectorState(st.director, deltas)
      for (const group of deltas.groups) addContractGroup(st, group.contractId, group.marker[0], group.marker[1], group.marker[2], group.tier)
      st.undockedAt = -1
      st.cursor = ctx.sim.events.head
    },
    deaths: (sim: Sim): void => encountersDeaths(st, sim),
    end: (sim: Sim): void => encountersEnd(st, sim),
  }
  states.set(runtime, st)
  return runtime
}
